/**
 * Shell controls — the sidebar command button and the organization/account menu.
 *
 * These are the two controls every workspace-shell portal needs and the two that
 * are easiest to re-implement slightly differently in each one. They were built
 * for Cascade and lived in that app, which meant MPF adopting the workspace
 * shell would have meant copying them a third time. They now sit beside AppShell
 * so Cascade, MPF, and anything after them render the same button, the same
 * popover, and the same sign-out affordance.
 *
 * Everything Cascade hardcoded is a prop here — the workspace subtitle, the
 * initials fallback, and the menu links, because a portal that has no Activity
 * route yet must not show a dead one.
 */

import type { LucideIcon } from 'lucide-react';
import { Check, ChevronDown, Command, LogOut, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useIsMobile } from '../hooks/use-media-query';
import { cn } from '../lib/utils';

export function SidebarCommandButton({
  compact,
  onOpen,
  label = 'Search or run a command',
}: {
  compact: boolean;
  onOpen: () => void;
  label?: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className={cn(
        'h-10 w-full justify-start border-border bg-background px-3 text-muted-foreground shadow-sm hover:text-foreground',
        compact && 'justify-center px-0',
      )}
      onClick={onOpen}
      aria-label="Open command palette"
    >
      <Search className="h-4 w-4 shrink-0" aria-hidden />
      {!compact && (
        <>
          <span className="truncate text-xs">{label}</span>
          <kbd className="ml-auto inline-flex items-center gap-0.5 rounded border bg-muted px-1.5 py-0.5 text-[10px]">
            <Command className="h-2.5 w-2.5" aria-hidden />K
          </kbd>
        </>
      )}
    </Button>
  );
}

/**
 * A menu entry. `section` groups entries under a heading; entries in the
 * `account` section render beneath the signed-in user rather than the
 * organization.
 */
export interface WorkspaceMenuLink {
  to: string;
  label: string;
  icon: LucideIcon;
  section?: 'organization' | 'account';
}

export function WorkspaceMenu({
  compact,
  organizationName,
  organizationSubtitle,
  userName,
  userEmail,
  links,
  fallbackInitials = '••',
  accountFallbackLabel = 'Signed in',
  organizationSectionLabel = 'Organization',
  onSignOut,
}: {
  compact: boolean;
  organizationName: string;
  /** The line under the organization name — what this workspace is. */
  organizationSubtitle?: string;
  userName?: string;
  userEmail?: string;
  links?: WorkspaceMenuLink[];
  fallbackInitials?: string;
  accountFallbackLabel?: string;
  organizationSectionLabel?: string;
  onSignOut: () => void;
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const accountLabel = userName || userEmail || accountFallbackLabel;
  const initials = getInitials(accountLabel, fallbackInitials);
  const organizationInitials = getInitials(organizationName, fallbackInitials);
  const organizationLinks = (links ?? []).filter((link) => link.section !== 'account');
  const accountLinks = (links ?? []).filter((link) => link.section === 'account');
  /*
    On a phone this control lives inside the navigation sheet, which already
    occupies 88vw. A popover opening to its right has nowhere to go: it either
    renders off-screen or gets squeezed into a strip too narrow to read, which
    is what made the menu unusable on mobile. Below `md` it opens downward
    instead, sized to the viewport rather than to a fixed 288px.
  */
  const isMobile = useIsMobile();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-h-12 w-full items-center gap-2.5 rounded-lg border border-transparent px-2 py-2 text-left transition-colors hover:border-border hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            compact && 'justify-center px-0',
          )}
          aria-label="Open organization and account menu"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emphasis text-xs font-semibold text-emphasis-foreground">
            {organizationInitials}
          </span>
          {!compact && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{organizationName}</span>
                {organizationSubtitle && (
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {organizationSubtitle}
                  </span>
                )}
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={isMobile ? 'bottom' : 'right'}
        align="start"
        sideOffset={8}
        collisionPadding={12}
        className={cn(
          'p-0',
          isMobile
            ? 'max-h-[70vh] w-[calc(100vw-3rem)] max-w-[320px] overflow-y-auto'
            : 'w-72',
        )}
      >
        <div className="border-b p-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emphasis text-sm font-semibold text-emphasis-foreground">
              {organizationInitials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{organizationName}</p>
              <p className="text-xs text-muted-foreground">Current organization</p>
            </div>
            <Check className="h-4 w-4 text-primary" aria-label="Selected organization" />
          </div>
        </div>

        {organizationLinks.length > 0 && (
          <div className="p-2">
            <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {organizationSectionLabel}
            </p>
            <div className="space-y-0.5">
              {organizationLinks.map((link) => (
                <MenuLink
                  key={`${link.to}:${link.label}`}
                  link={link}
                  pathname={pathname}
                  onNavigate={() => setOpen(false)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="border-t p-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            User
          </p>
          <div className="mb-2 flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {initials}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{accountLabel}</p>
              {userEmail && userEmail !== accountLabel && (
                <p className="truncate text-xs text-muted-foreground">{userEmail}</p>
              )}
            </div>
          </div>
          <div className="space-y-0.5">
            {accountLinks.map((link) => (
              <MenuLink
                key={`${link.to}:${link.label}`}
                link={link}
                pathname={pathname}
                onNavigate={() => setOpen(false)}
              />
            ))}
            <button
              type="button"
              className="flex min-h-9 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
              onClick={onSignOut}
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Sign Out
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MenuLink({
  link,
  pathname,
  onNavigate,
}: {
  link: WorkspaceMenuLink;
  pathname: string;
  onNavigate: () => void;
}) {
  const Icon = link.icon;
  const active = pathname === link.to;
  return (
    <Link
      to={link.to}
      onClick={onNavigate}
      className={cn(
        'flex min-h-9 items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted',
        active && 'bg-emphasis font-medium text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {link.label}
    </Link>
  );
}

/**
 * Two letters from the first two words, splitting on whitespace or `@` so an
 * email address yields the local part's initial rather than "d@".
 */
function getInitials(value: string, fallback: string): string {
  return (
    value
      .split(/\s|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || fallback
  );
}
